import { ScrollTrigger } from "@/lib/gsap";
import { uniqueId } from "lodash";
import React, { useEffect, useRef } from "react";
import { Container, Heading } from "./styles";

type Props = {
  hasIndex: boolean;
  heading: string;
  slug: string;
  onParagraphEntry: (id: string) => void;
};

const ParagraphTitle: React.FC<Props> = (props) => {
  const { heading, slug, onParagraphEntry, hasIndex } = props;
  const headingRef = useRef<HTMLHeadingElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const heading = headingRef.current;
    const container = containerRef.current;
    if (heading && container) {
      const id = uniqueId();
      const block = container.parentElement;
      let endTrigger;
      let isAfterHeading = false;
      let i = 0;
      if (block) {
        do {
          const htmlElement = block.children[i];
          if (isAfterHeading) {
            // If the current html element is the next paragraph title:
            //   The end trigger should be the element before it, so we assign it.
            // Else if the current html element is the last block of the page:
            //   It must be set as the end trigger.
            if (htmlElement.classList.contains("paragraph-title-block")) {
              endTrigger = block.children[i - 1];
            } else if (i === block.children.length - 1) {
              endTrigger = htmlElement;
            }
          } else if (htmlElement.isEqualNode(container)) {
            isAfterHeading = true;
          }
          i++;
        } while (
          typeof endTrigger === "undefined" &&
          i < block.children.length
        );
      }
      const handler = () => {
        onParagraphEntry(slug);
      };
      const scrollTrigger = ScrollTrigger.create({
        id,
        start: "-5rem",
        trigger: heading,
        endTrigger: endTrigger || heading,
        onEnter: handler,
        onEnterBack: handler,
      });
      const timeout = setTimeout(() => {
        scrollTrigger.refresh();
      }, 1000);
      return () => {
        clearTimeout(timeout);
        ScrollTrigger.getById(id)?.kill();
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Container ref={containerRef} className="paragraph-title-block">
      {heading && (
        <Heading id={slug} ref={headingRef} $hasIndex={hasIndex}>
          {heading}
        </Heading>
      )}
    </Container>
  );
};

export default ParagraphTitle;
